/**
 * MagicSign utility functions.
 * Pattern reference: useMagicSignLogic.tsx (feature logic), constants.ts (feature constants).
 *
 * NOTE: This file intentionally does NOT import from ./constants to avoid a circular
 * dependency (constants.ts re-exports from this file). Shared primitive values (sign-type
 * labels, default signing capacities, max signatories) live in the leaf module
 * ./signTypeConfig.ts, which both this file and constants.ts import from.
 */
import { ConfigMetaRaw, ConfigMeta, SignType, MagicSignRow, MagicSignStatus } from './types';
import { formatDateByFormat, DATE_FORMATS } from '../../utils/dateUtils';
import { SIGN_TYPE_LABELS, DEFAULT_SIGNING_CAPACITIES, MAX_SIGNATORIES_DEFAULT } from './signTypeConfig';

/**
 * A document is ready for links to be auto-generated once it is still in UPLOADED (links not yet
 * generated) AND all three column fields are populated: Document Type, Sign Type and at least one
 * Signatory. Once links are generated the row locks, so this only ever fires from UPLOADED.
 */
export function isReadyToGenerateLinks(row: MagicSignRow): boolean {
    return (
        row.status === MagicSignStatus.UPLOADED && !!row.documentType && !!row.signType && !!row.signatories && row.signatories.length > 0
    );
}

/**
 * Appends a compact timestamp suffix (DD_MM_HH_mm_ss) to an uploaded file's name, before its
 * extension, so re-uploading the same document after a cancel does not trip Mnemosyne's
 * duplicate-file rejection. e.g. "contract.pdf" → "contract_15_06_15_30_45.pdf".
 */
export function buildUniqueUploadFileName(originalName: string): string {
    const suffix = formatDateByFormat(Date.now(), DATE_FORMATS.FILENAME_SUFFIX_DD_MM_HH_MM_SS);
    const dot = originalName.lastIndexOf('.');
    if (dot <= 0) return `${originalName}_${suffix}`;
    return `${originalName.slice(0, dot)}_${suffix}${originalName.slice(dot)}`;
}

/**
 * Converts a SCREAMING_SNAKE_CASE or PascalCase backend string into a
 * human-readable label, e.g. "E_SIGN" → "E Sign", "AadhaarSign" → "Aadhaar Sign".
 * Used to map raw string arrays from config-meta into {id, label} options.
 */
export function prettifyConfigString(raw: string): string {
    return raw
        .replace(/_/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .trim()
        .split(' ')
        .filter((word) => word.length > 0)
        .map((word) => {
            // Keep all-caps tokens of length ≤ 3 as-is (e.g. NDA, KYC, IPA, DSC)
            if (word === word.toUpperCase() && word.length <= 3) return word;
            return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        })
        .join(' ');
}

/**
 * Maps the raw backend ConfigMeta (string arrays) into the normalised ConfigMeta
 * shape consumed by the FE ({id,label}[] items, enabledSignTypes applied as filter).
 * Single canonical mapping place — imported by useMagicSignLogic and useGlobalMagicSignLogic.
 */
export function mapConfigMetaRaw(raw: ConfigMetaRaw): ConfigMeta {
    const enabledSet = new Set<string>(raw.enabledSignTypes ?? []);

    const documentTypes = (raw.documentTypes ?? []).map((s) => ({ id: s, label: prettifyConfigString(s) }));

    // Only include sign types the backend explicitly enabled. If the backend sends an empty list,
    // default to E-Sign ONLY — never fall back to "all sign types" (that would surface the dormant
    // Aadhaar/DSC options, which are Phase-2 and gated off until Leegality is enabled per-PNO).
    const signTypes =
        enabledSet.size === 0
            ? [
                  {
                      id: SignType.E_SIGN,
                      label: SIGN_TYPE_LABELS[SignType.E_SIGN] ?? prettifyConfigString(SignType.E_SIGN),
                      enabled: true,
                  },
              ]
            : Object.values(SignType)
                  .filter((id) => enabledSet.has(id))
                  .map((id) => ({ id, label: SIGN_TYPE_LABELS[id] ?? prettifyConfigString(id), enabled: true }));

    const signingCapacities =
        raw.signingCapacities && raw.signingCapacities.length > 0
            ? raw.signingCapacities.map((s) => ({ id: s, label: prettifyConfigString(s) }))
            : DEFAULT_SIGNING_CAPACITIES;

    // Backend sends labels and colours as two flat maps; merge them into the
    // {label, color, bgColor} shape StatusCell consumes (bgColor = colour @ 10% alpha)
    const statusConfig = raw.statusLabels
        ? Object.fromEntries(
              Object.entries(raw.statusLabels).map(([status, label]) => {
                  const colour = raw.statusColours?.[status as keyof typeof raw.statusColours];
                  return [status, { label, color: colour ?? '#555', bgColor: colour ? `${colour}1A` : '#f5f5f5' }];
              }),
          )
        : undefined;

    return {
        documentTypes,
        signTypes,
        signingCapacities,
        maxSignatories: raw.maxSignatories ?? MAX_SIGNATORIES_DEFAULT,
        statusConfig,
    };
}

/**
 * Derives the row-level signingLinks array (consumed by the email composer and
 * Copy Signing Link) from the per-signatory signingLink the backend returns.
 */
export function deriveSigningLinks<T extends MagicSignRow>(row: T): T {
    if (row?.signingLinks?.length || !row?.signatories?.length) return row;
    const links = row.signatories
        .filter((s) => !!s.signingLink)
        .map((s) => ({ signatoryId: s.userId ?? s.email, link: s.signingLink as string, name: s.name, orgName: s.orgName }));
    return links.length ? { ...row, signingLinks: links } : row;
}

/**
 * True when `fileName` ends with one of `extensions` (case-insensitive). Shared by the Data Room
 * "Add to Magic Sign" action (File.tsx) and Magic Upload's "Add to Magic Sign" action
 * (MagicUploadFilesTable.tsx) so both use one signable-file rule.
 */
export function isSignableFileName(fileName: string, extensions: string[]): boolean {
    const lower = (fileName ?? '').toLowerCase();
    return extensions.some((ext) => lower.endsWith(ext.toLowerCase()));
}
